/** Explicit form answers drive triage. A routine gas log is not a gas alarm. */
export type SafetyPriority = 'urgent' | 'review' | 'routine';
export function safetyFormPriority(templateId: string, values: Record<string, unknown>): { priority: SafetyPriority; priorityReason: string } {
  const answer = (key: string) => String(values[key] ?? '').trim().toLowerCase();
  if (answer('alarmsOrExcursions') === 'yes' || answer('atmosphereAcceptable') === 'no') {
    return { priority: 'urgent', priorityReason: 'Reported gas alarm / unsafe atmosphere' };
  }
  if (['actualSeverity', 'potentialSeverity', 'severity'].some(key => ['critical', 'high'].includes(answer(key)))) {
    return { priority: 'urgent', priorityReason: 'Reported high / critical severity' };
  }
  if (templateId === 'incident-near-miss-report') return { priority: 'review', priorityReason: 'Incident / near miss' };
  if (templateId === 'h2s-atmospheric-gas-testing-log') return { priority: 'review', priorityReason: 'H₂S / gas test review' };
  if (['correctiveActionNeeded', 'h2sOrAtmosphereConcern', 'h2sOrGasConcern', 'stopWorkRequired'].some(key => answer(key) === 'yes')) {
    return { priority: 'review', priorityReason: 'Reported hazard / follow-up required' };
  }
  if (answer('monitorCalibrated') === 'no') return { priority: 'review', priorityReason: 'Gas monitor calibration issue' };
  return { priority: 'routine', priorityReason: 'Submitted form' };
}

/** D1 datetime('now') is UTC, even when its string has no timezone suffix. */
export function safetyTimestamp(value?: string | null): number {
  if (!value) return NaN;
  const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value) ? `${value.replace(' ', 'T')}Z` : value;
  return Date.parse(normalized);
}

export function safetyGpsFreshness(lastUpdated: string, now = Date.now()): 'current' | 'delayed' | 'stale' {
  const age = now - safetyTimestamp(lastUpdated);
  if (!Number.isFinite(age) || age < -30_000 || age > 15 * 60_000) return 'stale';
  return age <= 2 * 60_000 ? 'current' : 'delayed';
}

export function safetySpeedMph(speedMps: unknown): number | null {
  return typeof speedMps === 'number' && Number.isFinite(speedMps) && speedMps >= 0 && speedMps <= 100
    ? Math.round(speedMps * 2.2369362921) : null;
}
